import debounce from "lodash-es/debounce";
import {
  action,
  autorun,
  computed,
  type Lambda,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  makeObservable,
} from "mobx";
import { fromPromise } from "mobx-utils";

import Api from "nvent-web/services/Api";
import { type ProjectFilters } from "nvent-web/services/resources/ProjectResource";
import { type Project } from "nvent-web/types/Project";
import { type SearchState } from "nvent-web/types/SearchState";
import { createInitialFromPromise } from "nvent-web/utils/createInitialFromPromise";

export type Owner = "my" | "team";

export interface ProjectSearchFilters {
  query: string;
  status: SearchState;
  owner: Owner;
}

export type ProjectSearchInitialFilters = Partial<ProjectSearchFilters>;

export class ProjectSearch implements ProjectSearchFilters {
  query = "";
  status: SearchState = "open";
  owner: Owner = "my";
  debouncedSearchChanged;

  resultsPromise = createInitialFromPromise<Project[]>([]);
  results: Project[] = [];

  get areResultsLoading() {
    return this.resultsPromise.state === "pending";
  }

  private get filters(): ProjectFilters {
    return {
      search: this.query,
      state: this.status,
    };
  }

  constructor(
    private api: Api,
    initialFilters?: ProjectSearchInitialFilters
  ) {
    makeObservable(this, {
      query: observable,
      status: observable,
      owner: observable,
      resultsPromise: observable,
      results: observable,
      areResultsLoading: computed,
      loadResults: action,
      reloadResults: action,
      searchChanged: action,
      ownerChanged: action,
      setResults: action.bound,
    });

    if (initialFilters) {
      Object.assign(this, initialFilters);
    }

    this.debouncedSearchChanged = debounce(this.searchChanged, 200);

    autorun(() => {
      if (this.resultsPromise.state === "fulfilled") {
        this.setResults(this.resultsPromise.value);
      }
    });

    this.autoLoadResults();
  }

  setResults = (value: Project[]) => {
    this.results = value;
  };

  loadResults = async () => {
    return (this.resultsPromise = fromPromise(this.search()));
  };

  reloadResults = async () => {
    this.setResults([]);
    return this.loadResults();
  };

  searchChanged = (query: string) => {
    this.query = query.length >= 3 ? query : "";
  };

  ownerChanged = (owner: Owner) => {
    this.owner = owner;
  };

  autoLoadResults() {
    // when someone starts observing results, load them and keep reloading them
    // whenever filters change
    onBecomeObserved(this, "results", () => {
      this.loadResults();

      const disposers: Lambda[] = [
        reaction(() => this.query, this.loadResults),
        reaction(() => [this.status, this.owner], this.reloadResults),
        onBecomeUnobserved(this, "results", () => {
          disposers.forEach((disposer) => disposer());
        }),
      ];
    });
  }

  private async search(): Promise<Project[]> {
    const resource =
      this.owner === "my" ? this.api.projects : this.api.team.projects;
    return resource.search(this.filters).then(({ data }) => data);
  }
}
